/**
 * Second batch of first-party visualizations (written against PRELUDE; params map to uP[]).
 */
import type { VisualizationDef } from '../../../shared/viz';

export const kaleidoBloom: VisualizationDef = {
  id: 'kaleido-bloom',
  name: 'Kaleido Bloom',
  description: 'A kaleidoscopic flower of flowing noise that opens on the bass.',
  tags: ['energy:mid', 'style:psychedelic'],
  params: [
    { id: 'petals', label: 'Petals', type: 'float', min: 3, max: 16, default: 8, step: 1 },
    { id: 'flow', label: 'Flow', type: 'float', min: 0, max: 2, default: 0.6 },
    { id: 'zoom', label: 'Zoom', type: 'float', min: 0.5, max: 3, default: 1.2 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float r = length(p);
  float seg = TAU / floor(uP[0]);
  float a = mod(atan(p.y, p.x) + uTime * 0.05, seg);
  a = abs(a - seg * 0.5);
  vec2 q = vec2(cos(a), sin(a)) * r * uP[2] * (1.0 - 0.15 * uBass);
  float n = fbm(q * 3.0 + vec2(uTime * uP[1] * 0.3, -uTime * uP[1] * 0.2));
  n = fbm(q * 2.0 + n * 2.0);
  vec3 col = palette(n + r * 0.4 + uBarPhase * 0.25);
  col *= smoothstep(0.0, 0.6, n) * (0.6 + 0.6 * uLevel + 0.4 * pulse());
  col *= smoothstep(1.1, 0.1, r);
  fragColor = vec4(col, 1.0);
}`,
};

export const waterfall: VisualizationDef = {
  id: 'waterfall',
  name: 'Spectrogram Waterfall',
  description: 'The last second of the spectrum, scrolling like a studio analyser.',
  tags: ['energy:low', 'style:analyser'],
  params: [
    {
      id: 'direction',
      label: 'Direction',
      type: 'float',
      min: 0,
      max: 1,
      default: 0,
      step: 1,
      control: 'select',
      options: ['Downward', 'Sideways'],
    },
    { id: 'contrast', label: 'Contrast', type: 'float', min: 0.5, max: 3, default: 1.4 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float x = uP[0] > 0.5 ? uv.y : uv.x;
  float age = uP[0] > 0.5 ? 1.0 - uv.x : 1.0 - uv.y;
  float v = pow(spectrogram(x * 0.95, age), uP[1]);
  vec3 col = paletteClamped(v) * smoothstep(0.02, 0.25, v);
  col += palette(0.9) * smoothstep(0.012, 0.0, age) * spectrum(x * 0.95);
  fragColor = vec4(col, 1.0);
}`,
};

export const synthTerrain: VisualizationDef = {
  id: 'synth-terrain',
  name: 'Synth Terrain',
  description: 'Retro grid landscape raised by the spectrum history, under a striped sun.',
  tags: ['energy:mid', 'style:retro'],
  params: [
    { id: 'height', label: 'Height', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'sun', label: 'Sun', type: 'float', min: 0, max: 1, default: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float horizon = 0.05;
  vec3 col = mix(palette(0.05) * 0.25, palette(0.35) * 0.6, smoothstep(-0.2, 0.5, p.y));
  // Sun with stripes
  vec2 sp = p - vec2(0.0, 0.18);
  float sun = smoothstep(0.26, 0.25, length(sp));
  float stripes = step(0.5, fract((sp.y + uTime * 0.02) * 22.0)) + step(0.06, sp.y);
  col = mix(col, palette(0.85 - sp.y), sun * clamp(stripes, 0.0, 1.0) * uP[2] * (0.8 + 0.4 * pulse()));
  if (p.y < horizon) {
    float depth = 0.12 / (horizon - p.y + 0.02);
    float z = depth + uTime * uP[1];
    float x = p.x * depth;
    float age = clamp(1.0 - (depth - 0.1) / 6.0, 0.0, 1.0);
    float h = spectrogram(clamp(abs(x) * 0.25, 0.0, 1.0), 1.0 - age) * uP[0];
    vec2 g = abs(fract(vec2(x * 2.0, z) + 0.5) - 0.5) / fwidth(vec2(x * 2.0, z));
    float line = 1.0 - min(min(g.x, g.y), 1.0);
    vec3 ground = palette(0.15) * 0.15 + palette(0.6 + h * 0.4) * (line * (0.4 + h * 1.6));
    col = mix(ground, col, smoothstep(5.0, 9.0, depth));
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const metaballs: VisualizationDef = {
  id: 'metaballs',
  name: 'Metaballs',
  description: 'Liquid blobs orbiting and merging; each is driven by a frequency band.',
  tags: ['energy:mid', 'style:organic'],
  params: [
    { id: 'size', label: 'Size', type: 'float', min: 0.3, max: 2, default: 1 },
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 2, default: 0.6 },
    { id: 'edge', label: 'Edge glow', type: 'float', min: 0, max: 1, default: 0.6 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float field = 0.0;
  float hue = 0.0;
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float t = uTime * uP[1] * (0.4 + 0.07 * fi) + fi * 2.1;
    vec2 c = vec2(sin(t) * 0.55, cos(t * 1.3) * 0.32) * (0.6 + 0.4 * sin(fi + uTime * 0.1));
    float r = (0.05 + 0.09 * uBands[i]) * uP[0] * (1.0 + 0.3 * pulse());
    float f = r * r / max(dot(p - c, p - c), 1e-4);
    field += f;
    hue += f * fi / 8.0;
  }
  hue /= max(field, 1e-4);
  float inside = smoothstep(0.9, 1.1, field);
  float edge = smoothstep(0.6, 1.0, field) * (1.0 - inside) * uP[2] * 2.0;
  vec3 col = palette(hue + uBarPhase * 0.2) * (inside * (0.5 + 0.3 * smoothstep(1.0, 4.0, field)) + edge);
  fragColor = vec4(col, 1.0);
}`,
};

export const voronoiPulse: VisualizationDef = {
  id: 'voronoi-pulse',
  name: 'Voronoi Pulse',
  description: 'Drifting cells; each lights up with its frequency band and the beat.',
  tags: ['energy:mid', 'style:geometric'],
  params: [
    { id: 'cells', label: 'Cells', type: 'float', min: 3, max: 20, default: 8 },
    { id: 'drift', label: 'Drift', type: 'float', min: 0, max: 2, default: 0.5 },
    { id: 'border', label: 'Borders', type: 'float', min: 0, max: 1, default: 0.6 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = gl_FragCoord.xy / uResolution.y * uP[0];
  vec2 i = floor(p);
  vec2 f = fract(p);
  float d1 = 9.0, d2 = 9.0;
  vec2 best = vec2(0.0);
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 g = vec2(float(x), float(y));
      vec2 id = i + g;
      vec2 o = vec2(hash21(id), hash21(id + 19.7));
      o = 0.5 + 0.4 * sin(uTime * uP[1] + 6.2831 * o);
      float d = length(g + o - f);
      if (d < d1) { d2 = d1; d1 = d; best = id; } else if (d < d2) { d2 = d; }
    }
  }
  float h = hash21(best);
  float band = uBands[int(h * 7.99)];
  float lit = smoothstep(0.15, 0.9, band) * (0.6 + 0.6 * pulse());
  vec3 col = palette(h + uBarPhase * 0.3) * (0.08 + lit);
  col += palette(0.95) * (1.0 - smoothstep(0.0, 0.06, d2 - d1)) * uP[2] * (0.3 + uLevel);
  fragColor = vec4(col, 1.0);
}`,
};

export const aurora: VisualizationDef = {
  id: 'aurora',
  name: 'Aurora',
  description: 'Northern-lights curtains that brighten and ripple with the music.',
  tags: ['energy:low', 'style:ambient'],
  params: [
    { id: 'curtains', label: 'Curtains', type: 'float', min: 1, max: 5, default: 3, step: 1 },
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 2, default: 0.5 },
    { id: 'height', label: 'Height', type: 'float', min: 0.4, max: 2, default: 1 },
    {
      id: 'coverage',
      label: 'Coverage',
      type: 'float',
      min: 0,
      max: 1,
      default: 0,
    },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  // Coverage: 0 = curtains in the top of the sky, 1 = spread from the bottom of the frame to
  // the top, and taller, so they fill the screen.
  float cover = uP[3];
  vec3 col = palette(0.02) * 0.08 * mix(1.0 - uv.y, 1.0, cover);
  int n = int(uP[0]);
  float base0 = mix(0.15, -0.56, cover);
  float spacing = mix(0.12, n > 1 ? 0.78 / float(n - 1) : 0.0, cover);
  float tall = uP[2] * mix(1.0, 3.0, cover);
  for (int i = 0; i < 5; i++) {
    if (i >= n) break;
    float fi = float(i);
    float t = uTime * uP[1] * (0.3 + 0.1 * fi);
    float y0 = base0 + spacing * fi + 0.08 * sin(p.x * 2.0 + t + fi) + 0.05 * fbm(vec2(p.x * 3.0 + t, fi));
    float band = uBands[min(7, 2 + i)];
    float d = p.y - y0;
    float curtain = exp(-max(d, 0.0) * (6.0 - 3.0 * band) / tall) * smoothstep(-0.02, 0.01, d);
    float rays = 0.5 + 0.5 * fbm(vec2(p.x * 12.0 + t * 2.0, fi * 3.0));
    col += palette(0.3 + 0.15 * fi + 0.2 * d) * curtain * rays * (0.35 + 0.9 * band + 0.3 * pulse());
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const liquidChrome: VisualizationDef = {
  id: 'liquid-chrome',
  name: 'Liquid Chrome',
  description:
    'Slow, flowing liquid metal reflecting a studio sky in the palette’s colours. The song’s energy stirs it; the beat catches the highlights.',
  tags: ['energy:low', 'style:organic'],
  params: [
    { id: 'warp', label: 'Warp', type: 'float', min: 0, max: 2, default: 0.9 },
    { id: 'speed', label: 'Flow', type: 'float', min: 0, max: 1.5, default: 0.35 },
    { id: 'scale', label: 'Scale', type: 'float', min: 0.5, max: 3, default: 1.2 },
    { id: 'shine', label: 'Shine', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'tint', label: 'Palette tint', type: 'float', min: 0, max: 1, default: 0.35 },
  ],
  fragment: /* glsl */ `
// Smooth, low-frequency field: three octaves only, and audio enters through the slow song
// energy (not the raw bass), so the surface never jerks.
float smoothNoise(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 3; i++) { v += a * noise2(p); p = p * 1.9 + vec2(7.1, 3.3); a *= 0.5; }
  return v / 0.875;
}
float fieldAt(vec2 q) {
  float t = uTime * uP[1] * 0.25;
  vec2 w = vec2(smoothNoise(q + vec2(t, 0.3 * t)), smoothNoise(q + vec2(5.2, 1.3) - vec2(0.4 * t, t)));
  return smoothNoise(q + (uP[0] + 0.4 * uEnergy) * (w - 0.5) * 2.0);
}
// A soft studio environment: bright horizon band, dark floor, coloured by the palette.
vec3 envMap(vec3 r) {
  float y = r.y;
  vec3 skyTint = mix(vec3(0.82, 0.84, 0.88), palette(0.6 + 0.25 * y), uP[4]);
  vec3 sky = skyTint * (0.45 + 0.55 * smoothstep(-0.1, 0.9, y));
  vec3 floorCol = mix(vec3(0.16), palette(0.1) * 0.5, uP[4]) * 0.6;
  vec3 c = mix(floorCol, sky, smoothstep(-0.35, 0.1, y));
  c += vec3(1.0) * exp(-abs(y - 0.15) * 14.0) * 0.5;   // horizon softbox
  return c;
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y * 1.6 * uP[2];
  float e = 0.004 * uP[2];
  float h = fieldAt(p);
  vec3 n = normalize(vec3((fieldAt(p + vec2(e, 0.0)) - h) / e, (fieldAt(p + vec2(0.0, e)) - h) / e, 7.0));
  vec3 view = vec3(0.0, 0.0, -1.0);
  vec3 r = reflect(view, n);
  r = vec3(r.x, r.z * 0.6 + r.y * 0.8, r.y);
  vec3 col = envMap(normalize(r));
  float fres = pow(1.0 - max(n.z, 0.0), 2.0);
  vec3 l = normalize(vec3(0.4, 0.7, 1.0));
  float spec = pow(max(dot(reflect(-l, n), vec3(0.0, 0.0, 1.0)), 0.0), 60.0) * uP[3];
  col = col * (0.75 + 0.5 * fres) + vec3(spec) * (0.7 + 0.6 * pulse());
  fragColor = vec4(col, 1.0);
}`,
};

export const polarBars: VisualizationDef = {
  id: 'polar-bars',
  name: 'Polar Bars',
  description: 'Spectrum bars arranged around a circle, mirrored for symmetry.',
  tags: ['energy:high', 'style:classic'],
  params: [
    { id: 'bars', label: 'Bars', type: 'float', min: 16, max: 180, default: 96, step: 1 },
    { id: 'radius', label: 'Inner radius', type: 'float', min: 0.05, max: 0.4, default: 0.18 },
    { id: 'length', label: 'Length', type: 'float', min: 0.1, max: 0.8, default: 0.35 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float r = length(p);
  float a = atan(p.x, p.y) / TAU + 0.5;
  float m = abs(a * 2.0 - 1.0);
  float bars = floor(uP[0]);
  float idx = floor(m * bars * 0.5);
  float cell = fract(m * bars * 0.5);
  float s = spectrum((idx + 0.5) / (bars * 0.5) * 0.9);
  float inner = uP[1] * (1.0 + 0.2 * pulse());
  float outer = inner + 0.01 + s * uP[2];
  float bar = step(0.15, cell) * step(cell, 0.85) * step(inner, r) * step(r, outer);
  float glow = exp(-max(r - outer, 0.0) * 30.0) * step(inner, r) * step(0.15, cell) * step(cell, 0.85) * 0.3;
  vec3 col = palette(idx / bars * 2.0 * 0.8 + 0.1) * (bar * (0.6 + 0.4 * (r - inner) / max(outer - inner, 1e-3)) + glow);
  col += palette(0.5) * smoothstep(inner, 0.0, r) * 0.15 * (1.0 + pulse());
  fragColor = vec4(col, 1.0);
}`,
};

export const particleSwarm: VisualizationDef = {
  id: 'particle-swarm',
  name: 'Particle Swarm',
  description: 'Hundreds of glowing particles swirling around the centre; bass pushes them out.',
  tags: ['energy:high', 'style:particles'],
  params: [
    { id: 'density', label: 'Density', type: 'float', min: 4, max: 24, default: 12 },
    { id: 'spin', label: 'Spin', type: 'float', min: -2, max: 2, default: 0.6 },
    { id: 'size', label: 'Size', type: 'float', min: 0.5, max: 3, default: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  vec3 col = vec3(0.0);
  for (int layer = 0; layer < 4; layer++) {
    float fl = float(layer);
    float rr = length(p);
    float ang = atan(p.y, p.x) + uTime * uP[1] * (0.2 + 0.1 * fl) / (0.3 + rr);
    vec2 q = vec2(cos(ang), sin(ang)) * rr * (1.0 - 0.25 * uBass) * uP[0] + fl * 13.7;
    vec2 id = floor(q);
    vec2 f = fract(q) - 0.5;
    float h = hash21(id);
    vec2 off = (vec2(hash21(id + 1.7), hash21(id + 9.1)) - 0.5) * 0.7;
    float d = length(f - off);
    float size = (0.04 + 0.05 * h) * uP[2] * (1.0 + pulse());
    float glow = smoothstep(size, 0.0, d) + 0.25 * smoothstep(size * 4.0, 0.0, d);
    col += palette(h + fl * 0.1 + uBarPhase * 0.2) * glow * step(0.45, h) * (0.4 + uBands[layer * 2]);
  }
  col *= smoothstep(1.2, 0.0, length(p));
  fragColor = vec4(col, 1.0);
}`,
};

export const hexGrid: VisualizationDef = {
  id: 'hex-grid',
  name: 'Hex Grid',
  description: 'A honeycomb of tiles that ripple out from the centre on every beat.',
  tags: ['energy:high', 'style:geometric'],
  params: [
    { id: 'scale', label: 'Scale', type: 'float', min: 4, max: 30, default: 12 },
    { id: 'ripple', label: 'Ripple', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'gap', label: 'Gap', type: 'float', min: 0, max: 0.4, default: 0.12 },
  ],
  fragment: /* glsl */ `
vec4 hexCoords(vec2 uv) {
  const vec2 s = vec2(1.0, 1.7320508);
  vec4 c = floor(vec4(uv, uv - vec2(0.5, 1.0)) / s.xyxy) + 0.5;
  vec4 h = vec4(uv - c.xy * s, uv - (c.zw + 0.5) * s);
  return dot(h.xy, h.xy) < dot(h.zw, h.zw) ? vec4(h.xy, c.xy) : vec4(h.zw, c.zw + 0.5);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  vec4 h = hexCoords(p * uP[0]);
  float dist = length(h.zw) / uP[0];
  float edge = max(abs(h.x) * 0.866 + abs(h.y) * 0.5, abs(h.y));
  float tile = smoothstep(0.5 - uP[2] * 0.5, 0.48 - uP[2] * 0.5, edge);
  float wave = 0.5 + 0.5 * cos((dist * 6.0 - uBeatPhase) * TAU);
  float s = spectrum(clamp(dist * 1.2, 0.0, 1.0));
  float lit = pow(wave, 6.0) * uP[1] * (0.5 + pulse()) + s * 0.8;
  vec3 col = palette(dist * 1.5 + hash21(h.zw) * 0.2 + uBarPhase * 0.2) * tile * (0.06 + lit);
  fragColor = vec4(col, 1.0);
}`,
};

export const juliaDive: VisualizationDef = {
  id: 'julia-dive',
  name: 'Julia Dive',
  description: 'A Julia-set fractal whose shape morphs with the bass and mids.',
  tags: ['energy:mid', 'style:fractal'],
  params: [
    { id: 'morph', label: 'Morph', type: 'float', min: 0, max: 1, default: 0.5 },
    {
      id: 'detail',
      label: 'Detail',
      type: 'float',
      min: 32,
      max: 160,
      default: 96,
      step: 1,
      quality: true,
    },
    { id: 'zoom', label: 'Zoom', type: 'float', min: 0.5, max: 3, default: 1.3 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 z = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y * 2.6 / uP[2];
  float t = uTime * 0.07;
  vec2 c = vec2(-0.75 + 0.2 * cos(t) + 0.08 * uBass * uP[0], 0.15 + 0.15 * sin(t * 1.3) + 0.06 * uMid * uP[0]);
  float n = 0.0;
  float maxIt = floor(uP[1]);
  for (int i = 0; i < 160; i++) {
    if (float(i) >= maxIt || dot(z, z) > 16.0) break;
    z = vec2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + c;
    n += 1.0;
  }
  float sm = n - log2(max(1.0, log2(max(dot(z, z), 1.0001))));
  float v = sm / maxIt;
  vec3 col = n >= maxIt ? vec3(0.0) : palette(v * 3.0 + uBarPhase * 0.2) * (0.3 + 0.7 * sqrt(v)) * (0.8 + 0.4 * pulse());
  fragColor = vec4(col, 1.0);
}`,
};

export const ledEqualizer: VisualizationDef = {
  id: 'led-equalizer',
  name: 'LED Equalizer',
  description: 'Hardware-style segmented VU columns with peak-hold caps.',
  tags: ['energy:mid', 'style:retro'],
  params: [
    { id: 'columns', label: 'Columns', type: 'float', min: 8, max: 48, default: 24, step: 1 },
    { id: 'segments', label: 'Segments', type: 'float', min: 8, max: 40, default: 20, step: 1 },
    {
      id: 'colour',
      label: 'Colour mode',
      type: 'float',
      min: 0,
      max: 1,
      default: 0,
      step: 1,
      control: 'select',
      options: ['Palette by height', 'Palette by column'],
    },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float cols = floor(uP[0]);
  float segs = floor(uP[1]);
  float c = floor(uv.x * cols);
  float s = floor(uv.y * segs);
  vec2 cell = fract(vec2(uv.x * cols, uv.y * segs));
  float x = (c + 0.5) / cols * 0.92;
  float level = spectrum(x);
  float peak = 0.0;
  for (int k = 0; k < 16; k++) peak = max(peak, spectrogram(x, float(k) / 63.0));
  float onCell = step(s / segs, level);
  float peakCell = step(abs(s / segs - floor(peak * segs) / segs), 0.5 / segs) * step(level + 0.5 / segs, peak);
  float shape = step(0.12, cell.x) * step(cell.x, 0.88) * step(0.15, cell.y) * step(cell.y, 0.85);
  float t = uP[2] > 0.5 ? c / cols : s / segs;
  vec3 col = palette(t) * shape * (onCell * (0.85 + 0.3 * pulse()) + 0.06 + peakCell);
  fragColor = vec4(col, 1.0);
}`,
};

export const MORE_VISUALIZATIONS: VisualizationDef[] = [
  kaleidoBloom,
  waterfall,
  synthTerrain,
  metaballs,
  voronoiPulse,
  aurora,
  liquidChrome,
  polarBars,
  particleSwarm,
  hexGrid,
  juliaDive,
  ledEqualizer,
];
